import apiClient from "../../api/axios";

const PREFIX = "/v3/market-intelligence";
const DOCUMENT_CACHE_TTL_MS = 60_000;
const PAGE_CACHE_TTL_MS = 30_000;
const documentCache = new Map();
let overviewCache = null;
const searchCache = new Map();

function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unwrap(payload, validate, label) {
    let value = payload;
    if (isRecord(value) && "data" in value) value = value.data;
    if (isRecord(value) && "data" in value) value = value.data;
    if (!validate(value)) throw new Error(`${label}响应契约无效`);
    return value;
}

const isDocument = (value) =>
    isRecord(value) && typeof value.id === "string" && typeof value.title === "string" && typeof value.document_type === "string" && isRecord(value.deterministic_fields);
const isOverview = (value) => isRecord(value) && Array.isArray(value.latest_quotes) && Array.isArray(value.key_signals) && isRecord(value.counts) && isRecord(value.sync_status);
const isSearch = (value) => isRecord(value) && Array.isArray(value.items) && value.items.every(isDocument) && isRecord(value.pagination) && isRecord(value.available_filters);
const isEntity = (value) => isRecord(value) && typeof value.type === "string" && typeof value.key === "string" && Array.isArray(value.documents);

export async function getOverview({ force = false } = {}) {
    if (!force && overviewCache && Date.now() - overviewCache.createdAt < PAGE_CACHE_TTL_MS) {
        return overviewCache.promise;
    }
    const promise = apiClient
        .get(`${PREFIX}/overview`)
        .then((response) => unwrap(response.data, isOverview, "市场情报首页"))
        .catch((error) => {
            if (overviewCache?.promise === promise) overviewCache = null;
            throw error;
        });
    overviewCache = { createdAt: Date.now(), promise };
    return promise;
}

export async function searchDocuments(params, { force = false } = {}) {
    const cacheKey = JSON.stringify(params || {});
    const cached = searchCache.get(cacheKey);
    if (!force && cached && Date.now() - cached.createdAt < PAGE_CACHE_TTL_MS) {
        return cached.promise;
    }
    const promise = apiClient
        .get(`${PREFIX}/documents`, { params })
        .then((response) => unwrap(response.data, isSearch, "市场情报搜索"))
        .catch((error) => {
            if (searchCache.get(cacheKey)?.promise === promise) searchCache.delete(cacheKey);
            throw error;
        });
    searchCache.set(cacheKey, { createdAt: Date.now(), promise });
    return promise;
}

export async function getDocument(id, { force = false } = {}) {
    const cacheKey = String(id);
    const cached = documentCache.get(cacheKey);
    if (!force && cached && Date.now() - cached.createdAt < DOCUMENT_CACHE_TTL_MS) {
        return cached.promise;
    }

    const promise = apiClient
        .get(`${PREFIX}/documents/${encodeURIComponent(id)}`)
        .then((response) => unwrap(response.data, isDocument, "市场情报文档"))
        .catch((error) => {
            if (documentCache.get(cacheKey)?.promise === promise) documentCache.delete(cacheKey);
            throw error;
        });
    documentCache.set(cacheKey, { createdAt: Date.now(), promise });
    return promise;
}

export function prefetchDocument(id) {
    return getDocument(id).catch(() => null);
}

export async function getEntity(type, key) {
    return unwrap((await apiClient.get(`${PREFIX}/entities/${type}/${encodeURIComponent(key)}`)).data, isEntity, "市场情报实体");
}

export async function triggerSync() {
    return unwrap(
        (await apiClient.post(`${PREFIX}/sync`)).data,
        (value) => isRecord(value) && typeof value.id === "string" && typeof value.status === "string" && typeof value.already_running === "boolean",
        "市场情报手动同步",
    );
}

export async function requeueAnalysisJobs() {
    return unwrap(
        (await apiClient.post(`${PREFIX}/analysis-jobs/requeue`)).data,
        (value) => isRecord(value) && Number.isInteger(value.upgraded) && value.upgraded >= 0,
        "市场情报重新分析",
    );
}

export async function getAnalysisTrace(jobId) {
    return unwrap(
        (await apiClient.get(`${PREFIX}/analysis-jobs/${encodeURIComponent(jobId)}/trace`)).data,
        (value) => isRecord(value) && typeof value.job_id === "string" && Array.isArray(value.items),
        "市场情报分析轨迹",
    );
}
